import { useState, useEffect, useCallback } from 'react';
import { Brain, AlertTriangle, CheckCircle, TrendingDown, TrendingUp, Minus, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { useApi } from '@/hooks/useApi';

// ─── Types ────────────────────────────────────────────────────

interface RetentionRisk {
  employee_id: string;
  employee_name: string;
  title: string;
  department: string;
  risk_score: number;
  risk_level: 'low' | 'medium' | 'high' | 'critical';
  signals: string[];
  recommended_actions: string[];
}

interface TheoInsight {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  description: string;
  employee_id?: string;
  employee_name?: string;
  action?: string;
}

interface BriefingData {
  generated_at: string;
  day_of_week: string;
  summary: {
    total_at_risk: number;
    critical_count: number;
    medium_count: number;
    low_count: number;
  };
  top_risks: RetentionRisk[];
  insights: TheoInsight[];
}

interface TeamHealth {
  overall_score: number;
  flight_risk_count: number;
  overdue_reviews_count: number;
  pto_hoarding_count: number;
  expiring_certs_count: number;
  team_health_trend: 'improving' | 'stable' | 'declining';
  department_breakdown: Array<{
    department: string;
    headcount: number;
    avg_risk_score: number;
  }>;
}

// ─── Components ───────────────────────────────────────────────

function RiskBadge({ level }: { level: string }) {
  const styles: Record<string, string> = {
    critical: 'bg-red-500/20 text-red-400 border border-red-500/30',
    high: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    medium: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
    low: 'bg-green-500/20 text-green-400 border border-green-500/30',
  };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${styles[level] ?? styles['low']}`}>
      {level}
    </span>
  );
}

function SeverityIcon({ severity }: { severity: string }) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-red-400" />;
  if (severity === 'warning') return <AlertTriangle className="w-4 h-4 text-amber-400" />;
  return <CheckCircle className="w-4 h-4 text-green-400" />;
}

function TrendIcon({ trend }: { trend: string }) {
  if (trend === 'improving') return <TrendingUp className="w-5 h-5 text-green-400" />;
  if (trend === 'declining') return <TrendingDown className="w-5 h-5 text-red-400" />;
  return <Minus className="w-5 h-5 text-amber-400" />;
}

function RiskCard({ risk }: { risk: RetentionRisk }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="glass rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium text-foreground">{risk.employee_name}</span>
            <RiskBadge level={risk.risk_level} />
          </div>
          <p className="text-xs text-muted-foreground">{risk.title} &middot; {risk.department}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right">
            <span className="text-lg font-bold text-foreground">{risk.risk_score}</span>
            <span className="text-xs text-muted-foreground">/100</span>
          </div>
          <button onClick={() => setExpanded(!expanded)} className="text-muted-foreground hover:text-foreground">
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 pt-3 border-t border-border/50 space-y-3">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Risk Signals</p>
            <ul className="space-y-1">
              {risk.signals.map((signal, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-foreground">
                  <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                  {signal}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Recommended Actions</p>
            <ul className="space-y-1">
              {risk.recommended_actions.map((action, i) => (
                <li key={i} className="flex items-start gap-1.5 text-xs text-green-400">
                  <CheckCircle className="w-3 h-3 mt-0.5 shrink-0" />
                  {action}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ask THEO ─────────────────────────────────────────────────

function AskTheo() {
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function ask() {
    if (!question.trim()) return;
    setLoading(true);
    setError(null);
    setAnswer('');
    try {
      const res = await fetch('/api/people/theo/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (!res.ok) throw new Error('Failed to get response');
      const data = await res.json() as { answer: string };
      setAnswer(data.answer);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  const suggestions = [
    'How can I reduce turnover risk for my engineering team?',
    'What retention strategies work best for MSP technicians?',
    'When should I initiate a performance improvement plan?',
    'How do I handle a resignation of a key technician?',
  ];

  return (
    <div className="glass rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Brain className="w-5 h-5 text-amber-500" />
        <h3 className="text-sm font-semibold text-foreground">Ask THEO</h3>
      </div>

      <div className="flex gap-2">
        <input
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !loading) void ask(); }}
          placeholder="Ask THEO anything about your workforce..."
          className="flex-1 bg-surface-3 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground"
        />
        <button
          onClick={() => void ask()}
          disabled={loading || !question.trim()}
          className="px-4 py-2 rounded-lg bg-amber-500 text-foreground hover:bg-amber-600 text-sm disabled:opacity-50 flex items-center gap-1.5"
        >
          <Send className="w-3.5 h-3.5" />
          {loading ? 'Thinking...' : 'Ask'}
        </button>
      </div>

      {!answer && !loading && (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((s, i) => (
            <button key={i} onClick={() => setQuestion(s)}
              className="text-xs px-2.5 py-1 rounded-full bg-surface-3 text-muted-foreground hover:text-foreground hover:bg-surface-2 border border-border">
              {s}
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}

      {answer && (
        <div className="bg-surface-3 rounded-lg p-4 text-sm text-foreground whitespace-pre-wrap leading-relaxed">
          <div className="flex items-center gap-1.5 mb-2">
            <Brain className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-medium text-amber-500">THEO</span>
          </div>
          {answer}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────

export function TheoHRPage() {
  const { data: briefing, loading: briefingLoading } = useApi<BriefingData>('people/theo/briefing');
  const { data: teamHealth, loading: healthLoading } = useApi<TeamHealth>('people/theo/team-health');
  const [risks, setRisks] = useState<RetentionRisk[]>([]);
  const [risksLoading, setRisksLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState<string>('all');

  const loadRisks = useCallback(async () => {
    setRisksLoading(true);
    try {
      const params = filterLevel !== 'all' ? `?risk_level=${filterLevel}` : '';
      const res = await fetch(`/api/people/theo/retention-risks${params}`);
      if (!res.ok) throw new Error('Failed');
      const data = await res.json() as { risks: RetentionRisk[] };
      setRisks(data.risks);
    } catch {
      // silent
    } finally {
      setRisksLoading(false);
    }
  }, [filterLevel]);

  useEffect(() => { void loadRisks(); }, [loadRisks]);

  const loading = briefingLoading || healthLoading;

  if (loading) return <div className="text-muted-foreground text-sm">Loading THEO HR Advisor...</div>;

  const healthColor = (teamHealth?.overall_score ?? 0) >= 75 ? 'text-green-400' : (teamHealth?.overall_score ?? 0) >= 50 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
          <Brain className="w-5 h-5 text-amber-500" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-foreground">THEO HR Advisor</h1>
          <p className="text-sm text-muted-foreground">
            AI-powered workforce intelligence &middot; Last updated {briefing?.generated_at ? new Date(briefing.generated_at).toLocaleTimeString() : '--'}
          </p>
        </div>
      </div>

      {/* Team Health Score */}
      {teamHealth && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="glass rounded-xl p-4 col-span-2 md:col-span-1">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs text-muted-foreground">Team Health</p>
              <TrendIcon trend={teamHealth.team_health_trend} />
            </div>
            <p className={`text-3xl font-bold ${healthColor}`}>{teamHealth.overall_score}</p>
            <p className="text-xs text-muted-foreground capitalize">{teamHealth.team_health_trend}</p>
          </div>
          {[
            { label: 'Flight Risks', value: teamHealth.flight_risk_count, color: 'text-red-400' },
            { label: 'Overdue Reviews', value: teamHealth.overdue_reviews_count, color: 'text-orange-400' },
            { label: 'PTO Hoarding', value: teamHealth.pto_hoarding_count, color: 'text-amber-400' },
            { label: 'Expiring Certs', value: teamHealth.expiring_certs_count, color: 'text-amber-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="glass rounded-xl p-4">
              <p className="text-xs text-muted-foreground mb-1">{label}</p>
              <p className={`text-2xl font-bold ${value > 0 ? color : 'text-green-400'}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Weekly Insights */}
      {briefing && briefing.insights.length > 0 && (
        <div className="glass rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground">This Week&apos;s Insights</h3>
          <div className="space-y-2">
            {briefing.insights.map((insight, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-surface-3">
                <SeverityIcon severity={insight.severity} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{insight.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{insight.description}</p>
                  {insight.action && (
                    <p className="text-xs text-amber-400 mt-1">Action: {insight.action}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Retention Risks */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Retention Risks ({risks.length})</h3>
            <div className="flex gap-1">
              {(['all', 'critical', 'high', 'medium', 'low'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setFilterLevel(level)}
                  className={`px-2.5 py-1 rounded text-xs capitalize transition-colors ${filterLevel === level ? 'bg-amber-500/20 text-amber-400' : 'text-muted-foreground hover:text-foreground hover:bg-surface-3'}`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {risksLoading ? (
            <div className="text-muted-foreground text-sm">Loading...</div>
          ) : risks.length === 0 ? (
            <div className="glass rounded-xl p-8 text-center">
              <CheckCircle className="w-10 h-10 text-green-400/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No retention risks detected. Team is in great shape!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {risks.map(risk => <RiskCard key={risk.employee_id} risk={risk} />)}
            </div>
          )}
        </div>

        {/* Department Breakdown + Ask THEO */}
        <div className="space-y-4">
          {teamHealth && teamHealth.department_breakdown.length > 0 && (
            <div className="glass rounded-xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Department Risk</h3>
              <div className="space-y-2">
                {teamHealth.department_breakdown.slice(0, 8).map(dept => (
                  <div key={dept.department} className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-center mb-0.5">
                        <span className="text-xs text-foreground truncate">{dept.department}</span>
                        <span className="text-xs text-muted-foreground">{dept.avg_risk_score}</span>
                      </div>
                      <div className="h-1.5 bg-surface-3 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${dept.avg_risk_score >= 50 ? 'bg-red-500' : dept.avg_risk_score >= 30 ? 'bg-amber-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(dept.avg_risk_score, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <AskTheo />
        </div>
      </div>
    </div>
  );
}
